import { yupResolver } from '@hookform/resolvers/yup'
import { DateInput } from '@isdd/idsk-ui-kit/date-input/DateInput'
import { Button, ButtonGroupRow, IOption, Input, MultiSelect, SimpleSelect, TextArea } from '@isdd/idsk-ui-kit/index'
import { ATTRIBUTE_NAME } from '@isdd/metais-common/api'
import { ConfigurationItemUi } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { Attribute } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { CiLazySelect } from '@isdd/metais-common/components/ci-lazy-select/CiLazySelect'
import { DateTime } from 'luxon'
import React, { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Resolver, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { getDescriptionByAttribute, getNameByAttribute, getRequiredByAttribute } from '@/components/views/codeLists/CodeListDetailUtils'
import { REF_PORTAL_SUBMIT_ID } from '@/components/views/ref-identifiers/RefIdentifierCreateView'
import {
    RefDataItemFormType,
    RefDataItemFormTypeEnum,
    refIdentifierCreateDataItemSchema,
} from '@/components/views/ref-identifiers/forms/refCreateSchema'
import { compact, debounce, last, split } from 'lodash'

type RefDataItemFormPropsType = {
    onSubmit: (data: RefDataItemFormType, isSend: boolean) => void
    onCancel: () => void
    isUpdate: boolean
    ciItemData?: ConfigurationItemUi
    attributes: Attribute[] | undefined
    ownerOptions: IOption<string>[]
    templateUriOptions: IOption<string>[]
    dataItemTypeOptions: IOption<string>[]
    defaultDataItemTemplateUriUuids?: string[]
    defaultPo?: string
    isDisabled?: boolean
}

export const RefDataItemForm: React.FC<RefDataItemFormPropsType> = ({
    onSubmit,
    onCancel,
    isUpdate,
    ciItemData,
    attributes,
    ownerOptions,
    dataItemTypeOptions,
    templateUriOptions,
    defaultDataItemTemplateUriUuids,
    defaultPo,
    isDisabled,
}) => {
    const {
        t,
        i18n: { language },
    } = useTranslation()

    const attributeList = [
        ATTRIBUTE_NAME.Gen_Profil_nazov,
        ATTRIBUTE_NAME.Gen_Profil_anglicky_nazov,
        ATTRIBUTE_NAME.Gen_Profil_popis,
        ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku,
        ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku,
        ATTRIBUTE_NAME.Profil_DatovyPrvok_historicky_kod,
        ATTRIBUTE_NAME.Profil_DatovyPrvok_zaciatok_ucinnosti,
        ATTRIBUTE_NAME.Profil_DatovyPrvok_koniec_ucinnosti,
        ATTRIBUTE_NAME.Gen_Profil_kod_metais,
        ATTRIBUTE_NAME.Gen_Profil_ref_id,
        ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator,
    ]
    const attributesDefaultValues = Object.fromEntries(attributeList.map((item) => [item, ciItemData?.attributes?.[item]]))

    const methods = useForm<RefDataItemFormType>({
        defaultValues: {
            [RefDataItemFormTypeEnum.OWNER]: ownerOptions?.at(0)?.value,
            [RefDataItemFormTypeEnum.DATA_ITEM]: defaultDataItemTemplateUriUuids,
            [RefDataItemFormTypeEnum.PO]: defaultPo,
            attributes: attributesDefaultValues,
        },
        mode: 'onChange',
        resolver: yupResolver(refIdentifierCreateDataItemSchema(t, language, attributesDefaultValues, attributes)) as Resolver<RefDataItemFormType>,
    })

    const { register, formState, setValue, watch, handleSubmit, clearErrors, control, trigger } = methods

    useEffect(() => {
        if (defaultPo) setValue(RefDataItemFormTypeEnum.PO, defaultPo)
    }, [defaultPo, setValue])

    const { errors } = formState

    const buttonRefId = document.getElementById(REF_PORTAL_SUBMIT_ID)
    useEffect(() => {
        formState.isSubmitted && trigger()
    }, [formState.isSubmitted, t, trigger])

    const refIdField = useWatch({ control, name: `attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator}` })

    const debouncedSetCode = useMemo(
        () =>
            debounce((value: string) => {
                const parsed = last(compact(split(value, '/')))

                if (parsed) {
                    setValue(`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku}` as any, parsed, {
                        shouldValidate: true,
                        shouldDirty: true,
                    })
                }
            }, 500),
        [setValue],
    )

    useEffect(() => {
        if (!refIdField) return
        debouncedSetCode(refIdField)

        return () => debouncedSetCode.cancel()
    }, [refIdField, debouncedSetCode])

    return (
        <>
            <form noValidate>
                {!isUpdate && (
                    <SimpleSelect
                        label={`${t('refIdentifiers.create.ownerUser')} ${t('input.requiredField')}`}
                        options={ownerOptions ?? []}
                        setValue={setValue}
                        name={RefDataItemFormTypeEnum.OWNER}
                        isClearable={false}
                        clearErrors={clearErrors}
                        defaultValue={ownerOptions?.at(0)?.value}
                        error={errors?.[RefDataItemFormTypeEnum.OWNER]?.message}
                    />
                )}
                <Input
                    required={getRequiredByAttribute(attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_nazov))}
                    disabled={isDisabled}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_nazov),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_nazov),
                    )}
                    {...register(`attributes.${ATTRIBUTE_NAME.Gen_Profil_nazov}`)}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Gen_Profil_nazov]?.message}
                />

                <Input
                    required={getRequiredByAttribute(
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator),
                    )}
                    disabled={isDisabled}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator),
                    )}
                    {...register(`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator}`)}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_referencovatelny_identifikator]?.message}
                />

                <Input
                    required={getRequiredByAttribute(attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_anglicky_nazov))}
                    disabled={isDisabled}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_anglicky_nazov),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_anglicky_nazov),
                    )}
                    {...register(`attributes.${ATTRIBUTE_NAME.Gen_Profil_anglicky_nazov}`)}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Gen_Profil_anglicky_nazov]?.message}
                />

                <Input
                    disabled
                    required={getRequiredByAttribute(
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku),
                    )}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku),
                    )}
                    {...register(`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku}`)}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_kod_datoveho_prvku]?.message}
                    readOnly
                />

                <SimpleSelect
                    disabled={isDisabled}
                    required={getRequiredByAttribute(
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku),
                    )}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku),
                    )}
                    options={dataItemTypeOptions ?? []}
                    setValue={setValue}
                    name={`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku}`}
                    isClearable={false}
                    clearErrors={clearErrors}
                    defaultValue={attributesDefaultValues?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku]}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_typ_datoveho_prvku]?.message}
                />

                <Input
                    disabled={isDisabled}
                    required={getRequiredByAttribute(
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_historicky_kod),
                    )}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_historicky_kod),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_historicky_kod),
                    )}
                    {...register(`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_historicky_kod}`)}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_historicky_kod]?.message}
                />

                <CiLazySelect
                    ciType="PO"
                    label={t('refIdentifiers.create.poGestor')}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    name={RefDataItemFormTypeEnum.PO}
                    defaultValue={defaultPo}
                    metaAttributes={{
                        state: ['DRAFT'],
                    }}
                    required
                    error={errors?.[RefDataItemFormTypeEnum.PO]?.message}
                />

                <MultiSelect
                    label={t('refIdentifiers.create.dataElementType')}
                    name={RefDataItemFormTypeEnum.DATA_ITEM}
                    options={templateUriOptions || {}}
                    setValue={setValue}
                    value={watch()?.[RefDataItemFormTypeEnum.DATA_ITEM]?.map((item) => item ?? '')}
                    defaultValue={defaultDataItemTemplateUriUuids}
                    clearErrors={clearErrors}
                    error={errors?.[RefDataItemFormTypeEnum.DATA_ITEM]?.message}
                />

                <DateInput
                    required={getRequiredByAttribute(
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_zaciatok_ucinnosti),
                    )}
                    handleDateChange={(date, name) => date && setValue(name as keyof RefDataItemFormType, DateTime.fromJSDate(date).toISO() ?? '')}
                    setValue={setValue}
                    {...register(`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_zaciatok_ucinnosti}`)}
                    control={control}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_zaciatok_ucinnosti),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_zaciatok_ucinnosti),
                    )}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_zaciatok_ucinnosti]?.message}
                />

                <DateInput
                    required={getRequiredByAttribute(
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_koniec_ucinnosti),
                    )}
                    handleDateChange={(date, name) => date && setValue(name as keyof RefDataItemFormType, DateTime.fromJSDate(date).toISO() ?? '')}
                    setValue={setValue}
                    {...register(`attributes.${ATTRIBUTE_NAME.Profil_DatovyPrvok_koniec_ucinnosti}`)}
                    control={control}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_koniec_ucinnosti),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Profil_DatovyPrvok_koniec_ucinnosti),
                    )}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Profil_DatovyPrvok_koniec_ucinnosti]?.message}
                />

                <TextArea
                    rows={3}
                    required={getRequiredByAttribute(attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_popis))}
                    label={getNameByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_popis),
                    )}
                    info={getDescriptionByAttribute(
                        language,
                        attributes?.find((item) => item.technicalName === ATTRIBUTE_NAME.Gen_Profil_popis),
                    )}
                    {...register(`attributes.${ATTRIBUTE_NAME.Gen_Profil_popis}`)}
                    error={errors?.attributes?.[ATTRIBUTE_NAME.Gen_Profil_popis]?.message}
                />

                {buttonRefId &&
                    createPortal(
                        <ButtonGroupRow>
                            <Button onClick={handleSubmit((data) => onSubmit(data, true))} label={t('refIdentifiers.create.finishRequest')} />
                            <Button onClick={handleSubmit((data) => onSubmit(data, false))} label={t('refIdentifiers.create.saveRequest')} />
                            <Button variant="secondary" onClick={onCancel} label={t('refIdentifiers.create.cancelRequest')} />
                        </ButtonGroupRow>,
                        buttonRefId,
                    )}
            </form>
        </>
    )
}
